import type { CSSProperties } from 'react';
import { useFormContext } from 'react-hook-form';
import { Building2, Lock, Tag } from 'lucide-react';
import SectionCard from '../SectionCard';
import { useRoleEditorContext } from '../RoleEditorContext';
import { SOVEREIGN } from '../../../../epc/utils/epcProjectVisual';
import type { DeptKey, EpcRoleTag, RoleEditorFormValues } from '../types';

/**
 * EPC role tier options surfaced in the "Acts as" picker. Order matches the
 * canonical tier hierarchy (top → bottom) so users see the org-chart from
 * leadership to field. Values mirror Role.tag / EpcProjectMembership.role.
 */
const EPC_TAG_OPTIONS: Array<{ value: EpcRoleTag; label: string; hint: string }> = [
  { value: 'epc_head', label: 'EPC Head', hint: 'Top-of-org EPC leadership seat.' },
  { value: 'pmo', label: 'PMO', hint: 'Programme-level governance & oversight.' },
  { value: 'project_head', label: 'Project Head', hint: 'Owns one EPC project end-to-end.' },
  { value: 'project_coordinator', label: 'Project Coordinator', hint: 'Assists Project Head; cross-team coordination.' },
  { value: 'department_head', label: 'Department Head', hint: 'Runs a project department (Civil, Electrical, ...).' },
  { value: 'manager', label: 'Manager', hint: 'Manager under a department head; assigns engineers.' },
  { value: 'site_engineer', label: 'Site Engineer', hint: 'Field user; daily logs + DPR.' },
];

interface DeptStyle {
  activeBg: string;
  activeBorder: string;
  activeTextColor: string;
  activeShadow: string;
  tag: string;
  fullName: string;
}

const DEPT_STYLES: Record<DeptKey, DeptStyle> = {
  OMS: {
    activeBg: SOVEREIGN.bottle,
    activeBorder: SOVEREIGN.bottle,
    activeTextColor: '#fff',
    activeShadow: `0 4px 12px ${SOVEREIGN.bottle}33`,
    tag: 'Operations',
    fullName: 'OMS',
  },
  AMS: {
    activeBg: SOVEREIGN.brand,
    activeBorder: SOVEREIGN.brand,
    activeTextColor: '#fff',
    activeShadow: `0 4px 12px ${SOVEREIGN.brand}33`,
    tag: 'Asset Management',
    fullName: 'AMS',
  },
  EPC: {
    activeBg: SOVEREIGN.champagne,
    activeBorder: SOVEREIGN.champagne,
    activeTextColor: SOVEREIGN.title,
    activeShadow: `0 4px 12px ${SOVEREIGN.champagne}44`,
    tag: 'EPC Projects',
    fullName: 'EPC',
  },
};

const DEPTS: DeptKey[] = ['OMS', 'AMS', 'EPC'];

/**
 * Step 2 of 6 — Department.
 *
 * Picks which business unit owns the role. Locked once the role exists
 * (changing department would invalidate every existing permission grant).
 */
export default function DepartmentSection() {
  const { setValue, watch } = useFormContext<RoleEditorFormValues>();
  const { activeDept, setActiveDept, editingRole, isReadonly } =
    useRoleEditorContext();

  const isLocked = !!editingRole;
  const handleSelect = (dept: DeptKey) => {
    if (isLocked || isReadonly) return;
    setActiveDept(dept);
    setValue('department', dept, { shouldDirty: true });
  };

  const tag = watch('tag');
  const isEpc = activeDept === 'EPC';

  return (
    <SectionCard
      icon={Building2}
      title="Department"
      sub="Which business unit this role belongs to."
      step={2}
      totalSteps={6}
    >
      {isLocked ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <DepartmentChip
              dept={activeDept}
              isActive
              disabled
              onClick={() => undefined}
            />
            <span
              className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium"
              style={{
                background: SOVEREIGN.paper,
                border: `1px solid ${SOVEREIGN.cardBorder}`,
                color: SOVEREIGN.kpiCaption,
              }}
            >
              <Lock size={11} />
              Locked after creation
            </span>
          </div>
          <p
            className="text-[12px] leading-snug"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            Department can&apos;t be changed once the role exists. Create a new
            role if you need a different department.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            {DEPTS.map((dept) => (
              <DepartmentChip
                key={dept}
                dept={dept}
                isActive={activeDept === dept}
                disabled={isReadonly}
                onClick={() => handleSelect(dept)}
              />
            ))}
          </div>
          <p
            className="text-[11px]"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            Changing department clears selected permissions and reloads available
            modules.
          </p>
        </div>
      )}

      {/* EPC tier picker — required when department is EPC. */}
      {isEpc ? (
        <div
          className="mt-4 rounded-xl p-4"
          style={{
            border: `1px solid ${SOVEREIGN.champagne}`,
            background: 'rgba(201,169,97,0.07)',
          }}
        >
          <label
            className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wider mb-1.5"
            style={{ color: SOVEREIGN.bottle }}
          >
            <Tag size={12} />
            Acts as (EPC role tier)
            <span className="text-red-500 normal-case font-normal">*</span>
          </label>
          <select
            value={tag ?? ''}
            disabled={isReadonly}
            onChange={(e) =>
              setValue('tag', (e.target.value || undefined) as EpcRoleTag | undefined, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            className="w-full rounded-xl px-3 py-2.5 text-sm outline-none transition focus:ring-2 disabled:opacity-60"
            style={{
              border: `1px solid ${SOVEREIGN.inputBorder}`,
              background: SOVEREIGN.inputBg,
              color: SOVEREIGN.ink,
              '--tw-ring-color': `${SOVEREIGN.brand}33`,
            } as CSSProperties}
          >
            <option value="">Select an EPC tier…</option>
            {EPC_TAG_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p
            className="mt-1.5 text-[11px]"
            style={{ color: SOVEREIGN.bottle, opacity: 0.8 }}
          >
            Determines which EPC project workspace and capabilities this role
            gets when assigned to a project. Custom roles inherit the tier
            behaviour of the option you pick here.
          </p>
          {tag ? (
            <p
              className="mt-1 text-[11px]"
              style={{ color: SOVEREIGN.bottle }}
            >
              {EPC_TAG_OPTIONS.find((o) => o.value === tag)?.hint}
            </p>
          ) : null}
        </div>
      ) : null}
    </SectionCard>
  );
}

interface ChipProps {
  dept: DeptKey;
  isActive: boolean;
  disabled?: boolean;
  onClick: () => void;
}

function DepartmentChip({ dept, isActive, disabled, onClick }: ChipProps) {
  const style = DEPT_STYLES[dept];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={isActive}
      className="group flex flex-col items-center justify-center gap-1 px-5 py-4 rounded-xl border-2 transition-all duration-200 disabled:cursor-not-allowed"
      style={
        isActive
          ? {
              background: style.activeBg,
              borderColor: style.activeBorder,
              color: style.activeTextColor,
              boxShadow: style.activeShadow,
            }
          : {
              background: SOVEREIGN.cardBg,
              borderColor: SOVEREIGN.cardBorder,
              color: SOVEREIGN.kpiCaption,
            }
      }
    >
      <span className="text-[15px] font-semibold leading-none">
        {style.fullName}
      </span>
      <span
        className="text-[10px] font-medium uppercase tracking-wider leading-none mt-0.5"
        style={{ opacity: isActive ? 0.85 : 0.7 }}
      >
        {style.tag}
      </span>
    </button>
  );
}
